import { useReducer, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import HeaderBox from '../../../../components/generic/arpariBoxes/headerBox'
import FormattedTabs from '../../../../components/generic/formattedTabs'
import OuterBox from '../../../../components/generic/arpariBoxes/OuterBox'
import TemplateDetails from './detailsTabs/TemplateDetails'
import ReportRuns from './detailsTabs/ReportRuns'
import BankAccounts from './detailsTabs/BankAccounts'
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined'
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined'
import TocOutlinedIcon from '@mui/icons-material/TocOutlined'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { backendGetRequest, backendPostWithoutRefresh } from '../../../../helpers/callBackend'
import { errorMessage } from '../../../../helpers/errorMessageFunc'
import RunReportModal from '../../../../components/specific/reports-custom/RunReportModal'

const initialState = {
  name: '',
  groupType: '',
  groupName: '',
  columns: [],
  runs: [],
  displayedRuns: [],
  runsLoading: false,
  totalRunDates: -1,
  bankAccounts: [],
  loading: false,
}

const ACTION_TYPES = {
  SET_LOADING: 'SET_LOADING',
  LOAD_REPORT: 'LOAD_REPORT',
  SET_RUNS_LOADING: 'SET_RUNS_LOADING',
  SET_TOTAL_RUN_DATES: 'SET_TOTAL_RUN_DATES',
  APPEND_RUNS: 'APPEND_RUNS',
  REMOVE_RUNS: 'REMOVE_RUNS',
}

const setLoadingAction = (isLoading) => {
  return {
    type: ACTION_TYPES.SET_LOADING,
    payload: isLoading,
  }
}

const loadReportAction = (name, groupType, groupName, columns, runs, bankAccounts) => {
  return {
    type: ACTION_TYPES.LOAD_REPORT,
    payload: {
      name: name,
      groupType: groupType,
      groupName: groupName,
      columns: columns,
      runs: runs,
      bankAccounts: bankAccounts,
    },
  }
}

const setRunsLoadingAction = (isLoading) => {
  return {
    type: ACTION_TYPES.SET_RUNS_LOADING,
    payload: isLoading,
  }
}

const setTotalRunDatesAction = (totalDates) => {
  return {
    type: ACTION_TYPES.SET_TOTAL_RUN_DATES,
    payload: totalDates,
  }
}

const appendRunsAction = (newRuns) => {
  return {
    type: ACTION_TYPES.APPEND_RUNS,
    payload: newRuns,
  }
}

const removeRunsAction = (runDates) => {
  return {
    type: ACTION_TYPES.REMOVE_RUNS,
    payload: runDates,
  }
}

const reducer = (state, action) => {
  switch (action.type) {
    default:
      return state
    case ACTION_TYPES.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      }
    case ACTION_TYPES.LOAD_REPORT:
      return {
        ...state,
        name: action.payload.name || '',
        groupType: action.payload.groupType || '',
        groupName: action.payload.groupName || '',
        columns: action.payload.columns || [],
        runs: action.payload.runs || [],
        bankAccounts: action.payload.bankAccounts || [],
        loading: false,
      }
    case ACTION_TYPES.SET_RUNS_LOADING:
      return {
        ...state,
        runsLoading: action.payload,
      }
    case ACTION_TYPES.SET_TOTAL_RUN_DATES:
      return {
        ...state,
        totalRunDates: action.payload,
      }
    case ACTION_TYPES.APPEND_RUNS:
      
      const newRuns = [...state.runs, ...action.payload]
      const newUniqueDates = Array.from(
        newRuns
          .reduce((map, run) => {
            if (!map.has(run.run_date)) {
              map.set(run.run_date, run)
            }
            return map
          }, new Map())
          .values()
      )
      const loadingDone = state.runsLoading >= 0 && newRuns.length >= state.totalRunDates
      return {
        ...state,
        runsLoading: !loadingDone,
        runs: newRuns,
        displayedRuns: newUniqueDates,
      }
    case ACTION_TYPES.REMOVE_RUNS:
      const filteredRuns = state.runs.filter((run) => !action.payload.includes(run.run_date))
      const filteredDisplayedRuns = state.displayedRuns.filter((run) => !action.payload.includes(run.run_date))
      return {
        ...state,
        runs: filteredRuns,
        displayedRuns: filteredDisplayedRuns,
      }
  }
}

const getReportBuilder = async (dispatch, reportId) => {
  try {
    dispatch(setLoadingAction(true))
    const reportResp = await backendGetRequest('/api/reports/v2', {
      params: {
        report_builder_id: reportId,
        serializer: 'columns',
      },
    })
    const reportBuilder = reportResp?.data?.data
    if (!reportBuilder) {
      throw new Error('Report builder not found')
    }

    dispatch(
      loadReportAction(
        reportBuilder.name,
        reportBuilder.report_type,
        reportBuilder.group_name,
        reportBuilder.columns,
        reportBuilder.runs,
        reportBuilder.bankAccounts
      )
    )
  } catch (error) {
    errorMessage(error.message)
    dispatch(setLoadingAction(false))
  }
}

const getRunDatesPage = async (dispatch, reportId, pageNum) => {
  try {
    const runDatesResp = await backendGetRequest('/api/reports/v2/report-builder-run', {
      params: {
        report_builder_id: reportId,
        page_index: pageNum,
      },
    })
    const newDates = runDatesResp?.data?.data
    if (!newDates) {
      throw new Error('No date information returned for run')
    }
    dispatch(appendRunsAction(newDates))
  } catch (error) {
    errorMessage(error.message)
  }
}

const getRunDates = async (dispatch, reportId) => {
  try {
    dispatch(setRunsLoadingAction(true))
    const runDatesInfo = await backendGetRequest('/api/reports/v2/report-builder-run', {
      params: {
        report_builder_id: reportId,
        page_index: 0,
      },
    })
    const totalRecords = runDatesInfo?.data?.total_records
    const totalPages = runDatesInfo?.data?.total_pages
    dispatch(setTotalRunDatesAction(totalRecords))

    for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
      getRunDatesPage(dispatch, reportId, pageNum)
    }
  } catch (error) {
    errorMessage(error.message)
    dispatch(setRunsLoadingAction(false))
  }
}

const ReportsCustomDetailsPage = () => {
  const { id } = useParams()
  const [state, dispatch] = useReducer(reducer, initialState)

  const removeRuns = useCallback(
    (runDates) => {
      dispatch(removeRunsAction(runDates))
    },
    [dispatch]
  )

  useEffect(() => {
    getReportBuilder(dispatch, id)
  }, [id])

  useEffect(() => {
    getRunDates(dispatch, id)
  }, [dispatch, id])

  return (
    <OuterBox>
      <HeaderBox
        title={state?.name}
        body={
          <Stack spacing={1} direction='row' height='100%' alignItems='center'>
            <Stack direciton='column'>
              <Typography variant='overline' fontSize={12} sx={{ mb: 0, mr: 2 }} color='secondary' noWrap={true}>
                Grouping
              </Typography>
              <Typography sx={{ mt: -1, mr: 3 }}>{state?.groupType}</Typography>
            </Stack>
            <Stack direciton='column'>
              <Typography variant='overline' fontSize={12} sx={{ mb: 0, mr: 2 }} color='secondary' noWrap={true}>
                Group Name
              </Typography>
              <Typography sx={{ mt: -1, mr: 3 }}>{state?.groupName}</Typography>
            </Stack>
          </Stack>
        }
        actions={
          <RunReportModal
            reportName={state.name}
            columns={state.columns}
            onRunComplete={(newRuns) => {
              dispatch(appendRunsAction(newRuns))
            }}
          />
        }
      />
      <FormattedTabs
        defaultActiveKey={1}
        tabPanes={[
          {
            key: 0,
            title: (
              <Stack direction='row' spacing={1} alignItems='center'>
                <TocOutlinedIcon />
                <Typography>Template Details</Typography>
              </Stack>
            ),
            body: <TemplateDetails columns={state?.columns} reportId={id} />,
          },
          {
            key: 1,
            title: (
              <Stack direction='row' spacing={1} alignItems='center'>
                <ReceiptOutlinedIcon />
                <Typography>Report Runs</Typography>
              </Stack>
            ),
            body: (
              <ReportRuns
                reportName={state?.name}
                runs={state.runs}
                runDates={state.displayedRuns}
                loading={state?.runsLoading}
                removeRuns={removeRuns}
                reportId={id}
              />
            ),
          },
          {
            key: 2,
            title: (
              <Stack direction='row' spacing={1} alignItems='center'>
                <AccountBalanceOutlinedIcon />
                <Typography>Bank Accounts</Typography>
              </Stack>
            ),
            body: <BankAccounts reportId={id} />,
          },
        ]}
      />
    </OuterBox>
  )
}

export default ReportsCustomDetailsPage
